<script setup>
import { storeToRefs } from 'pinia';
import { useCommonImports } from '~/common/composables/common-imports.composable.js';
import { useNaavixStore } from '~/naavix/store/naavix.store';

const props = defineProps({
  isAgentSettingUp: {
    type: Boolean,
    required: true,
  },
  isAgentReady: {
    type: Boolean,
    required: true,
  },
  isAgentSetupFailed: {
    type: String,
    required: true,
  },
});

const emit = defineEmits(['initializeAgent']);

const { $t } = useCommonImports();

const naavix_store = useNaavixStore();
const { get_naavix_agent } = storeToRefs(naavix_store);
</script>

<template>
  <div class="h-full p-4">
    <div class="text-sm font-semibold text-gray-900 flex justify-center items-center gap-2 mt-2">
      <IconHawkTaskmapperAi />
      NaaviX
      <template v-if="!get_naavix_agent.is_enabled">
        (disabled)
      </template>
    </div>
    <div class="h-full flex flex-col gap-12 justify-center items-center -mt-3">
      <template v-if="props.isAgentSetupFailed">
        <div class="flex flex-col items-center">
          <div
            class="h-10 w-10 flex items-center justify-center rounded-full bg-error-600 mb-5"
          >
            <IconHawkAlertTriangle class="text-white" />
          </div>
          <div class="text-center text-lg font-semibold text-gray-900">
            {{ $t('Setup failed') }}
          </div>
          <div class="text-center text-sm font-normal text-gray-500">
            {{ (`We couldn't complete the setup process due to a problem fetching the latest data.`) }}
            <template v-if="props.isAgentSetupFailed !== 'Unknown error'">
              {{ $t('Reason') }}: {{ props.isAgentSetupFailed }}
            </template>
          </div>
        </div>
        <div v-if="props.isAgentSettingUp" class="flex flex-col gap-1 justify-center items-center min-h-[54px]">
          <IconHawkLoadingOne class="animate-spin" />
          <div class="text-xs font-normal text-gray-500">
            {{ $t('This may take a few seconds.') }}
          </div>
        </div>
        <div v-else class="flex flex-col items-center gap-1">
          <div
            class="flex justify-center items-center bg-[#107CB2] rounded text-white w-[200px] h-[34px] text-xs font-semibold cursor-pointer"
            @click="emit('initializeAgent')"
          >
            <template v-if="props.isAgentReady">
              {{ $t('Continue') }}
              <IconHawkArrowRight class="w-4 h-4 inline ml-3" />
            </template>
            <template v-else>
              <IconHawkRefreshCwOne class="w-4 h-4 inline mr-3" />
              {{ $t('Try again') }}
            </template>
          </div>
          <div class="text-xs font-normal text-gray-500 flex items-center gap-1">
            <template v-if="props.isAgentReady">
              <IconHawkCheckCircleGreen />
              {{ $t('Your AI agent is ready!') }}
            </template>
            <template v-else>
              {{ $t('Or contact support') }}
            </template>
          </div>
        </div>
      </template>
      <template v-else>
        <div class="flex flex-col items-center">
          <div
            class="h-10 w-10 flex items-center justify-center rounded-full bubble mb-5"
          >
            <IconHawkStarSix class="text-white" />
          </div>
          <div class="text-center text-lg font-semibold text-gray-900">
            {{ $t('Set up your AI agent') }}
          </div>
          <div class="text-center text-sm font-normal text-gray-500">
            {{ $t('To get started, your AI agent needs to fetch and organize data from your modules. This helps it provide accurate insights and support.') }}
          </div>
        </div>
        <div v-if="props.isAgentSettingUp" class="flex flex-col gap-1 justify-center items-center min-h-[54px]">
          <IconHawkLoadingOne class="animate-spin" />
          <div class="text-xs font-normal text-gray-500">
            {{ $t('This may take a few seconds.') }}
          </div>
        </div>
        <div v-else class="flex flex-col items-center gap-1">
          <div
            class="flex justify-center items-center bg-[#107CB2] rounded text-white w-[200px] h-[34px] text-xs font-semibold cursor-pointer"
            @click="emit('initializeAgent')"
          >
            <template v-if="props.isAgentReady">
              {{ $t('Continue') }}
              <IconHawkArrowRight class="w-4 h-4 inline ml-3" />
            </template>
            <template v-else>
              <IconHawkRocketTwo class="w-4 h-4 inline mr-3" />
              {{ $t('Initialize agent') }}
            </template>
          </div>
          <div class="text-xs font-normal text-gray-500 flex items-center gap-1">
            <template v-if="props.isAgentReady">
              <IconHawkCheckCircleGreen />
              {{ $t('Your AI agent is ready!') }}
            </template>
            <template v-else>
              {{ $t('This may take a few seconds. You only need to do this once.') }}
            </template>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<style scoped>
@import "../styles/stylesheet.scss";
</style>
